(() => {
  const SELECTOR = '[data-product-customizer]';

  function initialise(root) {
    if (!root || root.dataset.pcInitialised === 'true') return;
    root.dataset.pcInitialised = 'true';

    const formId = root.dataset.formId;
    const form = document.getElementById(formId);
    const modal = root.querySelector('[data-pc-modal]');
    const openButtons = root.querySelectorAll('[data-pc-open]');
    const closeButtons = modal?.querySelectorAll('[data-pc-close]') || [];
    const saveButton = modal?.querySelector('[data-pc-save]');
    const resetButton = modal?.querySelector('[data-pc-reset]');
    const fields = [...(modal?.querySelectorAll('[data-pc-measurement]') || [])];
    const unitButtons = [...(modal?.querySelectorAll('[data-pc-unit]') || [])];
    const unitLabels = [...(modal?.querySelectorAll('[data-pc-unit-label]') || [])];
    const unitProperty = root.querySelector('[data-pc-unit-property]');
    const statusProperty = root.querySelector('[data-pc-status-property]');
    const errorBox = modal?.querySelector('[data-pc-error]');
    const summary = root.querySelector('[data-pc-summary]');
    const summaryText = root.querySelector('[data-pc-summary-text]');
    const filePicker = modal?.querySelector('[data-pc-file-picker]');
    const fileName = modal?.querySelector('[data-pc-file-name]');
    const fileList = modal?.querySelector('[data-pc-file-list]');
    const fileSlots = [...(modal?.querySelectorAll('[data-pc-file-slot]') || [])];
    const imageCountProperty = root.querySelector('[data-pc-image-count]');
    const uploadCard = modal?.querySelector('[data-pc-upload-card]');
    const maxFiles = Math.max(
      1,
      Number.parseInt(uploadCard?.dataset.pcMaxFiles || String(fileSlots.length || 5), 10) || 5
    );
    const required = root.dataset.required !== 'false';

    if (!form || !modal || !fields.length) return;

    /*
     * Portal placeholder: Dawn or third-party product sections can apply
     * transform/contain rules, which make position:fixed behave like an inline
     * element. Moving the open modal to document.body guarantees a true
     * viewport overlay. It is restored to the snippet when closed.
     */
    const placeholder = document.createComment('product-customizer-modal');
    modal.parentNode.insertBefore(placeholder, modal);

    let currentUnit = 'inches';
    let complete = false;
    let lastFocused = null;
    let selectedFiles = [];
    let previewUrls = [];

    const setError = (message = '') => {
      if (!errorBox) return;
      errorBox.textContent = message;
      errorBox.hidden = !message;
    };

    const fileKey = (file) =>
      `${file.name}::${file.size}::${file.lastModified}::${file.type}`;

    const revokePreviewUrls = () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
      previewUrls = [];
    };

    const assignFileToSlot = (slot, file) => {
      slot.value = '';

      if (!file) {
        slot.disabled = true;
        return;
      }

      try {
        const transfer = new DataTransfer();
        transfer.items.add(file);
        slot.files = transfer.files;
        slot.disabled = false;
      } catch (error) {
        console.error('[Product customizer] Unable to assign selected image.', error);
        slot.disabled = true;
        throw new Error('Your browser could not prepare the selected images. Please try a current browser.');
      }
    };

    const renderSelectedFiles = () => {
      revokePreviewUrls();

      fileSlots.forEach((slot, index) => {
        assignFileToSlot(slot, selectedFiles[index] || null);
      });

      if (imageCountProperty) {
        imageCountProperty.value = String(selectedFiles.length);
      }

      if (fileName) {
        fileName.textContent = selectedFiles.length
          ? `${selectedFiles.length} of ${maxFiles} images selected`
          : 'No images selected';
      }

      if (!fileList) return;

      fileList.innerHTML = '';
      fileList.hidden = selectedFiles.length === 0;

      selectedFiles.forEach((file, index) => {
        const url = URL.createObjectURL(file);
        previewUrls.push(url);

        const item = document.createElement('div');
        item.className = 'pc-upload-preview';

        const image = document.createElement('img');
        image.src = url;
        image.alt = `Reference image ${index + 1}: ${file.name}`;

        const meta = document.createElement('span');
        meta.className = 'pc-upload-preview__meta';
        meta.textContent = `${index + 1}`;

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'pc-upload-preview__remove';
        remove.dataset.pcRemoveFile = String(index);
        remove.setAttribute('aria-label', `Remove ${file.name}`);
        remove.innerHTML = '&times;';

        item.appendChild(image);
        item.appendChild(meta);
        item.appendChild(remove);
        fileList.appendChild(item);
      });
    };

    const clearSelectedFiles = () => {
      selectedFiles = [];
      if (filePicker) filePicker.value = '';
      fileSlots.forEach((slot) => {
        slot.value = '';
        slot.disabled = true;
      });
      if (imageCountProperty) imageCountProperty.value = '0';
      revokePreviewUrls();
      if (fileList) {
        fileList.innerHTML = '';
        fileList.hidden = true;
      }
      if (fileName) fileName.textContent = 'No images selected';
    };

    const addSelectedFiles = (incomingFiles) => {
      const allowed = ['image/jpeg', 'image/png', 'image/webp'];
      const nextFiles = [...selectedFiles];
      const existingKeys = new Set(nextFiles.map(fileKey));

      for (const file of incomingFiles) {
        if (!allowed.includes(file.type)) {
          throw new Error(`${file.name} is not a JPG, PNG or WEBP image.`);
        }

        if (file.size > 8 * 1024 * 1024) {
          throw new Error(`${file.name} is larger than 8 MB.`);
        }

        const key = fileKey(file);
        if (!existingKeys.has(key)) {
          existingKeys.add(key);
          nextFiles.push(file);
        }
      }

      if (nextFiles.length > maxFiles) {
        throw new Error(`You can upload a maximum of ${maxFiles} images.`);
      }

      selectedFiles = nextFiles;
      renderSelectedFiles();
    };

    const portalToBody = () => {
      if (modal.parentNode !== document.body) document.body.appendChild(modal);
    };

    const restoreToRoot = () => {
      if (placeholder.parentNode && modal.parentNode !== placeholder.parentNode) {
        placeholder.parentNode.insertBefore(modal, placeholder.nextSibling);
      }
    };

    const openModal = () => {
      lastFocused = document.activeElement;
      portalToBody();
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('pc-lock-scroll');
      document.body.classList.add('pc-lock-scroll');

      window.requestAnimationFrame(() => {
        const panel = modal.querySelector('.pc-modal__panel');
        if (panel) panel.scrollTop = 0;
        fields[0]?.focus({ preventScroll: true });
      });
    };

    const closeModal = () => {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('pc-lock-scroll');
      document.body.classList.remove('pc-lock-scroll');
      restoreToRoot();
      lastFocused?.focus?.({ preventScroll: true });
    };

    const updateUnitUI = () => {
      unitButtons.forEach((button) => {
        const active = button.dataset.pcUnit === currentUnit;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      unitLabels.forEach((label) => {
        label.textContent = currentUnit === 'cm' ? 'cm' : 'in';
      });
      if (unitProperty) unitProperty.value = currentUnit;
    };

    const switchUnit = (nextUnit) => {
      if (!['inches', 'cm'].includes(nextUnit) || nextUnit === currentUnit) return;
      const multiplier = nextUnit === 'cm' ? 2.54 : 1 / 2.54;

      fields.forEach((field) => {
        const value = Number.parseFloat(field.value);
        if (Number.isFinite(value) && value > 0) {
          field.value = (value * multiplier).toFixed(2).replace(/\.00$/, '');
        }
      });

      currentUnit = nextUnit;
      updateUnitUI();
      complete = false;
      if (statusProperty) statusProperty.value = 'Not completed';
      if (summary) summary.hidden = true;
    };

    const validate = () => {
      let firstInvalid = null;

      fields.forEach((field) => {
        const value = Number.parseFloat(field.value);
        const invalid = !Number.isFinite(value) || value <= 0;
        field.classList.toggle('is-invalid', invalid);
        field.setAttribute('aria-invalid', invalid ? 'true' : 'false');
        if (invalid && !firstInvalid) firstInvalid = field;
      });

      if (firstInvalid) {
        setError('Please enter a valid value for every measurement.');
        firstInvalid.focus({ preventScroll: false });
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }

      setError('');
      return true;
    };

    const updateSummary = () => {
      const unitShort = currentUnit === 'cm' ? 'cm' : 'in';
      const values = fields
        .slice(0, 4)
        .map((field) => `${field.dataset.label}: ${field.value} ${unitShort}`);

      if (summaryText) {
        const imageSummary = selectedFiles.length
          ? ` • ${selectedFiles.length} reference image${selectedFiles.length === 1 ? '' : 's'}`
          : '';
        summaryText.textContent = `${values.join(' • ')} • ${fields.length} measurements total${imageSummary}`;
      }
      if (summary) summary.hidden = false;
    };

    const save = () => {
      if (!validate()) return;
      complete = true;
      if (statusProperty) statusProperty.value = 'Completed';
      updateSummary();
      closeModal();
    };

    const reset = () => {
      fields.forEach((field) => {
        field.value = '';
        field.classList.remove('is-invalid');
        field.removeAttribute('aria-invalid');
      });

      modal.querySelectorAll('select[form], textarea[form]').forEach((field) => {
        if (field.tagName === 'SELECT') field.selectedIndex = 0;
        else field.value = '';
      });

      clearSelectedFiles();

      currentUnit = 'inches';
      updateUnitUI();
      complete = false;
      if (statusProperty) statusProperty.value = 'Not completed';
      if (summary) summary.hidden = true;
      setError('');
      fields[0]?.focus();
    };

    openButtons.forEach((button) => button.addEventListener('click', openModal));
    closeButtons.forEach((button) => button.addEventListener('click', closeModal));
    saveButton?.addEventListener('click', save);
    resetButton?.addEventListener('click', reset);

    unitButtons.forEach((button) => {
      button.addEventListener('click', () => switchUnit(button.dataset.pcUnit));
    });

    fields.forEach((field) => {
      field.addEventListener('input', () => {
        field.classList.remove('is-invalid');
        field.removeAttribute('aria-invalid');
        complete = false;
        if (statusProperty) statusProperty.value = 'Not completed';
        if (summary) summary.hidden = true;
      });
    });

    filePicker?.addEventListener('change', () => {
      setError('');
      const incomingFiles = [...(filePicker.files || [])];

      if (!incomingFiles.length) return;

      try {
        addSelectedFiles(incomingFiles);
      } catch (error) {
        setError(error?.message || 'The selected images could not be added.');
      } finally {
        filePicker.value = '';
      }
    });

    fileList?.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-pc-remove-file]');
      if (!removeButton) return;

      const index = Number.parseInt(removeButton.dataset.pcRemoveFile, 10);
      if (!Number.isInteger(index) || index < 0 || index >= selectedFiles.length) return;

      selectedFiles.splice(index, 1);

      try {
        renderSelectedFiles();
        setError('');
      } catch (error) {
        setError(error?.message || 'The selected image could not be removed.');
      }
    });

    form.addEventListener(
      'submit',
      (event) => {
        if (!required || complete) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        openModal();
        setError('Please complete and save your measurements before adding this product to the cart.');
      },
      true
    );

    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeModal();
    });

    updateUnitUI();
  }

  const initialiseAll = () => {
    document.querySelectorAll(SELECTOR).forEach(initialise);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseAll);
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', initialiseAll);
})();
